import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState } from 'recoil';
import styled from 'styled-components';

import { Permissions } from 'src/app_deprecated/constants/PermissionsConstants';

import { Button } from 'src/app/components/lib/button';
import { PageLoader } from 'src/app/components/lib/loader';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import ActionsMenu from 'src/app/layout/main/actions/actions-menu';
import { useBeeFreeSetup } from 'src/app/pages/marketing/campaigns/beefree-embed/use-beefree-setup';
import { emptyTemplate } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/shared-slides/shared/empty-template';
import { useGetSavedEmailQuery } from 'src/app/queries/email-marketing/get-template';
import { useSendTestEmailMutation } from 'src/app/queries/email-marketing/send-test-email';
import { useSaveEmailTemplateMutation } from 'src/app/queries/email-marketing/upsert-template';
import { useSaveWorkflowCampaignMutation } from 'src/app/queries/marketing/upsert-workflow-campaign';
import { useSendTestPushMutation } from 'src/app/queries/push-marketing/send-test-push';
import { useSendTestSmsMutation } from 'src/app/queries/sms-marketing/send-test-sms';
import { useNotificationActions } from 'src/app/state/notifications';
import { usePermissionCheck } from 'src/app/state/user-permissions';

import { SlideContainer } from '../../../shared/styles';
import { useWorkflowBuilderContext } from '../../../wizard/builder-context';
import { getPayloadNodesWithUpdatedNode, getSaveWorkflowCampaignPayload } from '../../../wizard/helpers';
import { emailEditorNavigationState, isEmailEditorEnabled } from '../atoms/workflow-and-email-editor-atoms';

import { SendTestEmailModal } from './modals/send-test-email-modal';
import { SendTestPushModal } from './modals/send-test-push-modal';
import { SendTestSMSModal } from './modals/send-test-sms-modal';

import type { ActionNodeData, WorkflowCampaignNode } from '../models';
import type BeefreeSDK from '@beefree.io/sdk';
import type { Action } from 'src/app/layout/main/actions/actions-menu';
import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

export function BeefreeEditorBuilder() {
  const beeFreeContainerRef = useRef(null);
  const beeFreePlugin = useRef<BeefreeSDK | null>(null);
  const notifications = useNotificationActions();

  // perms
  const canSendCampaigns = usePermissionCheck([Permissions.SendCampaigns]);

  // state
  const [builderState] = useWorkflowBuilderContext();
  const [showSendTestEmailModal, setShowSendTestEmailModal] = useState(false);
  const [showSendTestSmsModal, setShowSendTestSmsModal] = useState(false);
  const [showSendTestPushModal, setShowSendTestPushModal] = useState(false);
  const [mergeTagPreviewActive, setMergeTagPreviewActive] = useState(false);
  const [beefreeEditing, setBeefreeEditing] = useState(false);
  const { marketingCampaignId, selectedNode, eventTypeId } = builderState;
  const [, setShowEditor] = useRecoilState(isEmailEditorEnabled);
  const [navState, setNavState] = useRecoilState(emailEditorNavigationState);
  const { isEditorLoading } = navState;

  const nodeData = selectedNode?.data as ActionNodeData;
  const savedTemplateId = nodeData?.config?.savedTemplateId;
  const selectedTemplatePreset = nodeData?.config?.selectedTemplatePreset;
  const savedEmailMarketingTemplatePresetId = nodeData?.config?.emailConfig?.marketingTemplatePresetId;
  const savedSMSMarketingTemplatePresetId = nodeData?.config?.smsConfig?.marketingTemplatePresetId;
  const emailIsActive = Boolean(nodeData?.config?.emailConfig);
  const smsIsActive = Boolean(nodeData?.config?.smsConfig);
  const pushIsActive = Boolean(nodeData?.config?.pushConfig);

  const abandonedCartV2 = useLDFlag<boolean>(LD.EMAIL_MARKETING_ABANDONED_CART_V2) ?? false;

  const {
    data: savedTemplate,
    isLoading: savedTemplateIsLoading,
    isError: getSavedTemplateIsError,
  } = useGetSavedEmailQuery({
    templateId: savedTemplateId,
  });

  const { mutateAsync: emailTemplateMutatorAsync } = useSaveEmailTemplateMutation();
  const { mutate: saveWorkflowMutator, mutateAsync: saveWorkflowCampaignAsync } = useSaveWorkflowCampaignMutation();
  const { mutate: sendTestEmailMutator } = useSendTestEmailMutation();
  const { mutate: sendTestSmsMutator } = useSendTestSmsMutation();
  const { mutate: sendTestPushMutator } = useSendTestPushMutation();

  useEffect(() => {
    if (!beefreeEditing && canSendCampaigns) {
      setNavState((prev) => ({
        ...prev,
        isEditorLoading: false,
      }));
    } else {
      setNavState((prev) => ({
        ...prev,
        isEditorLoading: true,
      }));
    }
  }, [beefreeEditing, canSendCampaigns, setNavState]);

  const savePriorToTesting = async () => {
    await saveWorkflowCampaignAsync(getSaveWorkflowCampaignPayload({}, builderState));
  };

  // bee-free plugin and handlers
  const [beefreeIsLoading, setBeeFreeIsLoading] = useState(true);
  const beeFreeEnabled = (emailIsActive || smsIsActive) ?? false;
  const waitingForBeefree = beefreeIsLoading && beeFreeEnabled;

  // template for pre-existing templates
  const parsedSavedTemplateJSON = useMemo(
    () => (savedTemplate?.JsonBody ? JSON.parse(savedTemplate.JsonBody) : null),
    [savedTemplate?.JsonBody]
  );

  // template for template preset init
  const parsedSelectedTemplatePresetJSON = useMemo(
    () => (selectedTemplatePreset?.JsonBody ? JSON.parse(selectedTemplatePreset.JsonBody) : null),
    [selectedTemplatePreset?.JsonBody]
  );

  const templatePresetForInit = parsedSavedTemplateJSON ?? parsedSelectedTemplatePresetJSON ?? emptyTemplate;

  const templateJsonForBeeFreeInstance = templatePresetForInit;

  const handleBFReady = useCallback(() => {
    setBeeFreeIsLoading(false);
    beeFreePlugin.current?.save();
  }, []);

  const handleSave = useCallback(
    async (templateJSON: string, templateHTML: string) => {
      try {
        // Check if the HTML contains the dynamic campaign addon id
        const isDynamicTemplate = templateHTML.includes('isDynamicCampaignAddOn');

        const res = await emailTemplateMutatorAsync({
          JsonBody: templateJSON,
          HtmlBody: templateHTML,
          MarketingEmailTemplateId: savedTemplateId ?? 0,
          IsDynamic: isDynamicTemplate,
        });

        if (res.templateId && selectedNode && nodeData) {
          const updatedNode: WorkflowCampaignNode = {
            ...selectedNode,
            data: {
              ...nodeData,
              config: {
                ...nodeData.config,
                savedTemplateId: res.templateId,
                emailConfig: nodeData?.config.emailConfig?.isActive
                  ? {
                      ...nodeData.config.emailConfig,
                      marketingTemplatePresetId:
                        savedEmailMarketingTemplatePresetId ??
                        selectedTemplatePreset?.MarketingTemplatePresetId ??
                        null,
                    }
                  : null,
                smsConfig: nodeData?.config.smsConfig?.isActive
                  ? {
                      ...nodeData.config.smsConfig,
                      marketingTemplatePresetId:
                        savedSMSMarketingTemplatePresetId ?? selectedTemplatePreset?.MarketingTemplatePresetId ?? null,
                    }
                  : null,
              },
            },
          };

          saveWorkflowMutator(
            getSaveWorkflowCampaignPayload(
              {
                Nodes: getPayloadNodesWithUpdatedNode(builderState, updatedNode),
              },
              builderState
            ),
            {
              onSuccess: () => {
                setBeefreeEditing(false);
              },
              onError: (e) => {
                const error = e as ApiErrorResponse;
                const { data, status } = error.response;
                // a 400 error will return a user-friendly error message
                if (status === 400) {
                  notifications.error({ message: data.Message });
                } else {
                  notifications.error({ message: 'Error saving email template' });
                }
              },
            }
          );
        }
      } catch (error) {
        console.error(error);
        notifications.error({ message: 'Error saving email template' });
        setBeefreeEditing(false);
      }
    },
    [
      builderState,
      emailTemplateMutatorAsync,
      notifications,
      saveWorkflowMutator,
      savedTemplateId,
      selectedNode,
      nodeData,
      emailIsActive,
      smsIsActive,
      selectedTemplatePreset?.MarketingTemplatePresetId,
    ]
  );

  const handleChange = useCallback(() => {
    setBeefreeEditing(true);
    beeFreePlugin.current?.save();
  }, [beeFreePlugin]);

  const saveCallbackRef = useRef(handleSave);

  useEffect(() => {
    saveCallbackRef.current = handleSave;
  }, [handleSave]);

  const { beeFreeInstance } = useBeeFreeSetup({
    waitForTemplateLoad: !!savedTemplateId && savedTemplateIsLoading,
    beeFreeContainerRef,
    templateJSON: templateJsonForBeeFreeInstance,
    onSave: (...args) => saveCallbackRef.current(...args),
    onReady: handleBFReady,
    onChange: handleChange,
    isEnabled: beeFreeEnabled,
    containerId: 'bee-plugin-container-workflows',
    // Enable for abandoned cart workflows only
    // temporarily disable tile for SMS campaigns - until Cloudflare worker update for SMS landing pages is rolled out
    enableAbandonedCartAddOn: abandonedCartV2 && eventTypeId === 3 && !smsIsActive,
  });

  useEffect(() => {
    beeFreePlugin.current = beeFreeInstance;
  }, [beeFreeInstance]);

  if (getSavedTemplateIsError) {
    notifications.error({
      message: 'Could not load saved email template. You can still create new content or try again later.',
    });
  }

  const actions: Action[] = [];

  if (emailIsActive || smsIsActive) {
    actions.push({
      label: 'Preview',
      onClick: () => beeFreePlugin.current?.preview(),
      visible: true,
    });

    actions.push({
      label: `${mergeTagPreviewActive ? 'Hide' : 'Show'} merge tags Preview`,
      onClick: () => {
        beeFreePlugin.current?.toggleMergeTagsPreview();
        setMergeTagPreviewActive((prev) => !prev);
      },
      visible: true,
    });
  }

  if (emailIsActive) {
    actions.push({
      label: 'Send test email',
      onClick: async () => {
        if (!marketingCampaignId) {
          notifications.error({ message: 'You must save the campaign before sending a test email' });
          return;
        }
        await savePriorToTesting();
        setShowSendTestEmailModal(true);
      },
      visible: true,
    });
  }

  if (smsIsActive) {
    actions.push({
      label: 'Send test SMS',
      onClick: async () => {
        if (!marketingCampaignId) {
          notifications.error({ message: 'You must save the campaign before sending a test SMS' });
          return;
        }
        await savePriorToTesting();
        setShowSendTestSmsModal(true);
      },
      visible: true,
    });
  }

  if (pushIsActive) {
    actions.push({
      label: 'Send test push',
      onClick: async () => {
        if (!marketingCampaignId) {
          notifications.error({ message: 'You must save the campaign before sending a test push' });
          return;
        }
        await savePriorToTesting();
        setShowSendTestPushModal(true);
      },
      visible: true,
    });
  }

  const handleCloseEditor = () => {
    setShowEditor(false);

    setNavState((prev) => ({
      ...prev,
      hideWizardBottomNavigation: false,
    }));

    setNavState((prev) => ({
      ...prev,
      currentStep: 0,
    }));
  };

  return (
    <StyledSlideContainer>
      {waitingForBeefree && (
        <LoaderContainer>
          <PageLoader />
        </LoaderContainer>
      )}
      <EditorContainer $isLoading={waitingForBeefree}>
        <EditorHeader>
          <Button
            disabled={isEditorLoading}
            label='Close editor'
            variant='secondary'
            onClick={() => handleCloseEditor()}
          />
          <StyledActionsMenu actions={actions} />
        </EditorHeader>
        {beeFreeEnabled && <BFContainer id='bee-plugin-container-workflows' ref={beeFreeContainerRef} />}
      </EditorContainer>

      <SendTestEmailModal
        isOpen={showSendTestEmailModal}
        onClose={() => setShowSendTestEmailModal(false)}
        onSendClick={(sendTo) => {
          if (!marketingCampaignId) {
            setShowSendTestEmailModal(false);
            return;
          }
          sendTestEmailMutator(
            { MarketingCampaignId: marketingCampaignId, RecipientEmail: sendTo },
            {
              onSuccess: () => notifications.success({ message: 'Test email sent successfully' }),
              onError: (e) => {
                const err = e as ApiErrorResponse;
                notifications.error({ message: err.response.data.Message ?? 'Failed to send test email' });
              },
            }
          );
          setShowSendTestEmailModal(false);
        }}
      />
      <SendTestSMSModal
        isOpen={showSendTestSmsModal}
        onClose={() => setShowSendTestSmsModal(false)}
        onSendClick={(sendTo) => {
          if (!marketingCampaignId) {
            setShowSendTestSmsModal(false);
            return;
          }
          sendTestSmsMutator(
            { MarketingCampaignId: marketingCampaignId, PhoneNumber: sendTo },
            {
              onSuccess: () => notifications.success({ message: 'Test SMS sent successfully' }),
              onError: (e) => {
                const err = e as ApiErrorResponse;
                notifications.error({ message: err.response.data.Message ?? 'Failed to send test SMS' });
              },
            }
          );
          setShowSendTestSmsModal(false);
        }}
      />
      <SendTestPushModal
        isOpen={showSendTestPushModal}
        onClose={() => setShowSendTestPushModal(false)}
        onSendClick={(sendTo) => {
          if (!marketingCampaignId) {
            setShowSendTestPushModal(false);
            return;
          }
          sendTestPushMutator(
            { MarketingCampaignId: marketingCampaignId, RecipientEmail: sendTo },
            {
              onSuccess: () => notifications.success({ message: 'Test push sent successfully' }),
              onError: (e) => {
                const err = e as ApiErrorResponse;
                notifications.error({ message: err.response.data.Message ?? 'Failed to send test push' });
              },
            }
          );
          setShowSendTestPushModal(false);
        }}
      />
    </StyledSlideContainer>
  );
}

const StyledSlideContainer = styled(SlideContainer)`
  overflow-y: hidden;
`;
const EditorContainer = styled.div<{ $isLoading: boolean }>`
  display: ${({ $isLoading }) => ($isLoading ? 'none' : 'block')};
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;
const EditorHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: var(--sizes-50) var(--sizes-60);
  background-color: white;
  border-bottom: 1px solid var(--color-gray-20);
  top: 0px;
`;
const StyledActionsMenu = styled(ActionsMenu)`
  background-color: var(--color-brand-primary-white);
  border: 1px solid var(--color-gray-70);
`;
const BFContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: hidden;
`;
const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
